/**
 * <Textarea> — UI primitive added by `external-sharing` Phase 3 Session 3.2
 * (2026-05-09). Mirrors `<Input>`'s shape (`forwardRef` + label slot +
 * className passthrough) but renders a multi-line `<textarea>`. Default
 * `rows={4}`. Tokens match `<Input>` (`bg-input-bg` / `border-input-border`
 * / `text-text-bright` / `font-mono` / `focus:border-accent/20`).
 *
 * Used by `<SendDealLinkModal>` for the optional `message` field
 * (maxLength: 500 per spec §5).
 */
import { type TextareaHTMLAttributes, forwardRef } from 'react';
import clsx from 'clsx';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, className, rows = 4, ...props }, ref) => {
    return (
      <div className="flex items-start gap-2.5">
        {label && (
          <span className="text-sm text-text-muted font-mono w-36 shrink-0 pt-3.5">{label}</span>
        )}
        <textarea
          ref={ref}
          rows={rows}
          className={clsx(
            'w-full py-3.5 px-4 bg-input-bg border border-input-border rounded-[3px]',
            'text-text-bright text-md font-mono outline-none resize-y',
            'focus:border-accent/20',
            'placeholder:text-text-dim',
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
